import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    ScrollView,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    Alert,
    Image,
    KeyboardAvoidingView,
    Platform,
    ActivityIndicator,
    Modal
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { listBusiness, addMarketplaceItem, getBusinessDetails, deleteMarketplaceItem, updateMarketplaceItem } from '../../api/marketplace';
import { getShadow } from '../../theme/useTheme';
import { getImageUrl } from '../../utils/imageUtils';

const CreateBusinessScreen = ({ navigation, route }) => {
    const isEditing = route.params?.isEditing || false;
    const existingBusiness = route.params?.business || null;

    const [formData, setFormData] = useState({
        business_name: existingBusiness?.business_name || '',
        category: existingBusiness?.category?.name || '',
        description: existingBusiness?.description || '',
        location: existingBusiness?.location || '',
        phone: existingBusiness?.phone || '',
        whatsapp: existingBusiness?.whatsapp || '',
        email: existingBusiness?.email || '',
        businessType: existingBusiness?.business_type || '',
        yearsInOperation: existingBusiness?.yearsInOperation || '',
    });

    const [logo, setLogo] = useState(existingBusiness?.logo_url || null);
    const [businessPhotos, setBusinessPhotos] = useState(existingBusiness?.photos || []);
    const [loading, setLoading] = useState(false);

    // Items management
    const [items, setItems] = useState(existingBusiness?.items || []);
    const [isItemModalVisible, setIsItemModalVisible] = useState(false);
    const [itemLoading, setItemLoading] = useState(false);
    const [newItem, setNewItem] = useState({
        name: '',
        description: '',
        price: '',
        unit: 'piece',
        item_type: 'product',
        category: 'General',
        stock_quantity: '-1',
        service_config: {
            duration_estimate: '',
            location_type: 'In-Person',
            availability: ''
        },
        images: []
    });

    useEffect(() => {
        if (isEditing && existingBusiness?.id) {
            // Re-fetch full details to get real items if they weren't passed completely
            fetchFullBusinessDetails();
        }
    }, [isEditing]);

    const fetchFullBusinessDetails = async () => {
        try {
            const data = await getBusinessDetails(existingBusiness.id);
            setItems(data.items || []);
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const categories = [
        'Agriculture', 'Technology', 'Healthcare', 'Education', 'Manufacturing', 'Retail', 'Finance', 'Construction', 'Transportation', 'Hospitality', 'Media', 'Entertainment', 'Real Estate', 'Other'
    ];

    const businessTypes = [
        'Sole Proprietorship', 'Partnership', 'Limited Company', 'Cooperative', 'Social Enterprise', 'Other'
    ];

    const pickLogo = async () => {
        let result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.Images,
            allowsEditing: true,
            aspect: [1, 1],
            quality: 0.8,
        });

        if (!result.canceled) {
            setLogo(result.assets[0].uri);
        }
    };

    const handleInputChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
    };

    const pickItemImage = async () => {
        let result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.Images,
            allowsEditing: true,
            aspect: [1, 1],
            quality: 0.8,
        });

        if (!result.canceled) {
            setNewItem({ ...newItem, images: [...newItem.images, result.assets[0].uri] });
        }
    };

    const prepareFile = async (uri) => {
        if (!uri) return null;
        const filename = uri.split('/').pop();
        const match = /\.(\w+)$/.exec(filename);
        const ext = match ? match[1] : 'jpg';
        const type = `image/${ext === 'base64' ? 'jpeg' : (ext === 'jpg' ? 'jpeg' : ext)}`;

        if (Platform.OS === 'web') {
            const response = await fetch(uri);
            const blob = await response.blob();
            return new File([blob], filename || `upload.${ext}`, { type });
        }

        return {
            uri: Platform.OS === 'android' ? uri : uri.replace('file://', ''),
            name: filename || `upload.${ext}`,
            type: type
        };
    };

    const handleEditItem = (item) => {
        setNewItem({
            id: item.id,
            name: item.name,
            description: item.description,
            price: String(item.price),
            unit: item.unit,
            item_type: item.item_type,
            category: item.category || 'General',
            stock_quantity: String(item.stock_quantity ?? -1),
            service_config: item.service_config ? (typeof item.service_config === 'string' ? JSON.parse(item.service_config) : item.service_config) : { duration_estimate: '', location_type: 'In-Person', availability: '' },
            images: item.images ? (typeof item.images === 'string' ? JSON.parse(item.images) : item.images) : []
        });
        setIsItemModalVisible(true);
    };

    const handleDeleteItem = async (index, item) => {
        Alert.alert(
            "Delete Item",
            "Are you sure you want to delete this item?",
            [
                { text: "Cancel", style: "cancel" },
                {
                    text: "Delete",
                    style: "destructive",
                    onPress: async () => {
                        if (isEditing && item.id && !String(item.id).startsWith('temp_')) {
                            // Delete from backend
                            try {
                                await deleteMarketplaceItem(item.id);
                                setItems(items.filter((_, i) => i !== index));
                            } catch (e) {
                                Alert.alert("Error", "Failed to delete item");
                            }
                        } else {
                            // Just local state
                            setItems(items.filter((_, i) => i !== index));
                        }
                    }
                }
            ]
        );
    };

    const handleSaveItem = async () => {
        if (!newItem.name || !newItem.price) {
            Alert.alert('Error', 'Please provide at least a name and price for the item.');
            return;
        }

        // --- LOCAL STATE ONLY (New Business) ---
        if (!isEditing) {
            if (newItem.id) {
                // Edit local item
                const updatedItems = items.map(i => i.id === newItem.id ? newItem : i);
                setItems(updatedItems);
            } else {
                // Add new local item
                setItems([...items, { ...newItem, id: Date.now() }]);
            }
            setIsItemModalVisible(false);
            setNewItem({ name: '', description: '', price: '', unit: 'piece', item_type: 'product', category: 'General', stock_quantity: '-1', images: [] });
            return;
        }

        // --- BACKEND SYNC (Editing Business) ---
        try {
            setItemLoading(true);
            const data = new FormData();
            data.append('name', newItem.name);
            data.append('description', newItem.description);
            data.append('price', newItem.price);
            data.append('unit', newItem.unit);
            data.append('item_type', newItem.item_type);
            data.append('category', newItem.category);
            data.append('stock_quantity', newItem.stock_quantity);
            data.append('service_config', JSON.stringify(newItem.service_config));

            // Separate new images (URI) from existing (URL)
            const newImagesToUpload = newItem.images.filter(img => !img.startsWith('http'));
            const existingImages = newItem.images.filter(img => img.startsWith('http'));

            if (existingImages.length > 0) {
                data.append('existingImages', JSON.stringify(existingImages));
            }

            for (const uri of newImagesToUpload) {
                const file = await prepareFile(uri);
                if (file) data.append('images', file);
            }

            if (newItem.id && !String(newItem.id).startsWith('temp_') && newItem.id < 1000000000000) {
                // It's a real backend ID (simple check, backend IDs are usually ints < timestamps)
                // Actually safer to check if we fetched it from backend. 
                // Let's assume if it has an ID and we are in edit mode, and it's not a fresh timestamp, it's real.
                // Ideally we track 'isNew' flag. For now, assume if ID exists it's an update.

                console.log('Updating Item:', newItem.id);
                await updateMarketplaceItem(newItem.id, data);
            } else {
                console.log('Adding New Item to existing business');
                await addMarketplaceItem(data);
            }

            await fetchFullBusinessDetails(); // Refresh list
            setIsItemModalVisible(false);
            setNewItem({ name: '', description: '', price: '', unit: 'piece', item_type: 'product', category: 'General', stock_quantity: '-1', images: [] });
            Alert.alert('Success', 'Item saved successfully!');

        } catch (error) {
            console.error('Error saving item:', error);
            Alert.alert('Error', 'Failed to save item.');
        } finally {
            setItemLoading(false);
        }
    };

    const handleSubmit = async () => {
        const { business_name, category, description, phone } = formData;

        if (!business_name || !category || !description || !phone) {
            Alert.alert('Error', 'Please fill in all required fields (Name, Category, Description, Phone)');
            return;
        }

        try {
            setLoading(true);

            const data = new FormData();
            Object.keys(formData).forEach(key => {
                data.append(key, formData[key]);
            });

            if (logo && !logo.startsWith('http')) {
                const logoFile = await prepareFile(logo);
                if (logoFile) data.append('logo', logoFile);
            }

            for (const photoUri of businessPhotos) {
                if (typeof photoUri === 'string' && !photoUri.startsWith('http')) {
                    const photoFile = await prepareFile(photoUri);
                    if (photoFile) data.append('businessPhotos', photoFile);
                }
            }

            await listBusiness(data);

            Alert.alert(
                'Success',
                isEditing ? 'Business updated successfully!' : 'Your business has been listed! It is now visible on the marketplace.',
                [{ text: 'OK', onPress: () => navigation.goBack() }]
            );
        } catch (error) {
            console.error('Error creating business:', error);
            Alert.alert('Error', error || 'Failed to list business. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
            <View style={styles.container}>
                {/* Header */}
                <View style={styles.header}>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                        <Ionicons name="arrow-back" size={24} color="#111827" />
                    </TouchableOpacity>
                    <Text style={styles.headerTitle}>{isEditing ? 'Edit Business' : 'List Your Business'}</Text>
                    <View style={{ width: 40 }} />
                </View>

                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
                    {/* Logo Upload */}
                    <View style={styles.logoSection}>
                        <TouchableOpacity style={styles.logoCircle} onPress={pickLogo}>
                            {logo ? (
                                <Image source={{ uri: getImageUrl(logo) }} style={styles.logoImage} />
                            ) : (
                                <View style={styles.logoPlaceholder}>
                                    <Ionicons name="camera" size={32} color="#9CA3AF" />
                                    <Text style={styles.logoLabel}>Logo</Text>
                                </View>
                            )}
                        </TouchableOpacity>
                        <Text style={styles.logoHint}>{isEditing ? 'Update your business logo' : 'Pick a clear logo for your business'}</Text>
                    </View>

                    {/* Business Photos */}
                    <View style={styles.photosSection}>
                        <Text style={styles.inputLabel}>Business Photos (up to 5)</Text>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.photosList}>
                            {businessPhotos.map((photo, index) => (
                                <View key={index} style={styles.photoWrapper}>
                                    <Image source={{ uri: getImageUrl(typeof photo === 'string' ? photo : photo.url) }} style={styles.photoThumb} />
                                    <TouchableOpacity
                                        style={styles.removePhoto}
                                        onPress={() => setBusinessPhotos(businessPhotos.filter((_, i) => i !== index))}
                                    >
                                        <Ionicons name="close-circle" size={20} color="#EF4444" />
                                    </TouchableOpacity>
                                </View>
                            ))}
                            {businessPhotos.length < 5 && (
                                <TouchableOpacity style={styles.addPhotoButton} onPress={async () => {
                                    let result = await ImagePicker.launchImageLibraryAsync({
                                        mediaTypes: ImagePicker.MediaTypeOptions.Images,
                                        allowsEditing: true,
                                        aspect: [4, 3],
                                        quality: 0.8,
                                    });
                                    if (!result.canceled) {
                                        setBusinessPhotos([...businessPhotos, result.assets[0].uri]);
                                    }
                                }}>
                                    <Ionicons name="add" size={24} color="#6366F1" />
                                </TouchableOpacity>
                            )}
                        </ScrollView>
                    </View>

                    {/* Products & Services Section */}
                    <View style={styles.sectionDivider} />
                    <View style={styles.itemsSection}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionSubtitle}>Products & Services</Text>
                            <TouchableOpacity
                                style={styles.addItemButton}
                                onPress={() => setIsItemModalVisible(true)}
                            >
                                <Ionicons name="add-circle" size={20} color="#6366F1" />
                                <Text style={styles.addItemText}>Add Item</Text>
                            </TouchableOpacity>
                        </View>

                        {items.length === 0 ? (
                            <Text style={styles.noItemsText}>No products or services listed yet.</Text>
                        ) : (
                            items.map((item, index) => (
                                <View key={item.id || index} style={styles.itemCard}>
                                    <TouchableOpacity
                                        style={styles.itemCardContent}
                                        onPress={() => handleEditItem(item)}
                                    >
                                        <Image
                                            source={{ uri: getImageUrl(item.images?.[0] || (typeof item.image === 'string' ? item.image : 'https://via.placeholder.com/100')) }}
                                            style={styles.itemThumb}
                                        />
                                        <View style={styles.itemInfo}>
                                            <Text style={styles.itemName}>{item.name}</Text>
                                            <Text style={styles.itemPrice}>KES {item.price} / {item.unit}</Text>
                                        </View>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        onPress={() => handleDeleteItem(index, item)}
                                        style={styles.deleteItem}
                                    >
                                        <Ionicons name="trash-outline" size={20} color="#EF4444" />
                                    </TouchableOpacity>
                                </View>
                            ))
                        )}
                    </View>
                    <View style={styles.sectionDivider} />

                    {/* Form Fields */}
                    <View style={styles.form}>
                        <Text style={styles.inputLabel}>Business Name *</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. Sarah's Tailoring"
                            value={formData.business_name}
                            onChangeText={(text) => handleInputChange('business_name', text)}
                        />

                        <Text style={styles.inputLabel}>Category *</Text>
                        <View style={styles.categoryContainer}>
                            {categories.map((cat) => (
                                <TouchableOpacity
                                    key={cat}
                                    style={[
                                        styles.categoryChip,
                                        formData.category === cat && styles.categoryChipSelected
                                    ]}
                                    onPress={() => handleInputChange('category', cat)}
                                >
                                    <Text style={[
                                        styles.categoryChipText,
                                        formData.category === cat && styles.categoryChipTextSelected
                                    ]}>{cat}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>

                        <Text style={styles.inputLabel}>Business Type</Text>
                        <View style={styles.categoryContainer}>
                            {businessTypes.map((type) => (
                                <TouchableOpacity
                                    key={type}
                                    style={[
                                        styles.categoryChip,
                                        formData.businessType === type && styles.categoryChipSelected
                                    ]}
                                    onPress={() => handleInputChange('businessType', type)}
                                >
                                    <Text style={[
                                        styles.categoryChipText,
                                        formData.businessType === type && styles.categoryChipTextSelected
                                    ]}>{type}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>

                        <Text style={styles.inputLabel}>Years in Operation</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. 2"
                            keyboardType="numeric"
                            value={formData.yearsInOperation}
                            onChangeText={(text) => handleInputChange('yearsInOperation', text)}
                        />

                        <Text style={styles.inputLabel}>Description *</Text>
                        <TextInput
                            style={[styles.input, styles.textArea]}
                            placeholder="Tell us about your business, products, or services..."
                            multiline
                            numberOfLines={4}
                            value={formData.description}
                            onChangeText={(text) => handleInputChange('description', text)}
                        />

                        <Text style={styles.inputLabel}>Location</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. Nairobi, CBD"
                            value={formData.location}
                            onChangeText={(text) => handleInputChange('location', text)}
                        />

                        <Text style={styles.inputLabel}>Phone Number *</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. +254 700 000 000"
                            keyboardType="phone-pad"
                            value={formData.phone}
                            onChangeText={(text) => handleInputChange('phone', text)}
                        />

                        <Text style={styles.inputLabel}>WhatsApp Number</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. +254 700 000 000"
                            keyboardType="phone-pad"
                            value={formData.whatsapp}
                            onChangeText={(text) => handleInputChange('whatsapp', text)}
                        />

                        <Text style={styles.inputLabel}>Email Address</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. business@example.com"
                            keyboardType="email-address"
                            autoCapitalize="none"
                            value={formData.email}
                            onChangeText={(text) => handleInputChange('email', text)}
                        />

                        <TouchableOpacity
                            style={[styles.submitButton, loading && styles.submitButtonDisabled]}
                            onPress={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <ActivityIndicator color="#FFFFFF" />
                            ) : (
                                <Text style={styles.submitButtonText}>{isEditing ? 'Save Business Profile' : 'Create Listing'}</Text>
                            )}
                        </TouchableOpacity>
                    </View>
                </ScrollView>

                {/* Add Item Modal */}
                <Modal
                    visible={isItemModalVisible}
                    animationType="slide"
                    transparent={true}
                >
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalContent}>
                            <View style={styles.modalHeader}>
                                <Text style={styles.modalTitle}>Add Product or Service</Text>
                                <TouchableOpacity onPress={() => setIsItemModalVisible(false)}>
                                    <Ionicons name="close" size={24} color="#111827" />
                                </TouchableOpacity>
                            </View>

                            <ScrollView
                                style={styles.modalForm}
                                showsVerticalScrollIndicator={false}
                            >
                                <Text style={styles.inputLabel}>Item Name *</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="e.g. Custom Summer Dress"
                                    value={newItem.name}
                                    onChangeText={(text) => setNewItem({ ...newItem, name: text })}
                                />

                                <View style={styles.row}>
                                    <View style={{ flex: 1, marginRight: 8 }}>
                                        <Text style={styles.inputLabel}>Price (KES) *</Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="2500"
                                            keyboardType="numeric"
                                            value={newItem.price}
                                            onChangeText={(text) => setNewItem({ ...newItem, price: text })}
                                        />
                                    </View>
                                    <View style={{ flex: 1, marginLeft: 8 }}>
                                        <Text style={styles.inputLabel}>Unit</Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="piece/hour"
                                            value={newItem.unit}
                                            onChangeText={(text) => setNewItem({ ...newItem, unit: text })}
                                        />
                                    </View>
                                </View>

                                {newItem.item_type === 'product' && (
                                    <View>
                                        <Text style={styles.inputLabel}>Stock Quantity (-1 for unlimited)</Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="e.g. 50"
                                            keyboardType="numeric"
                                            value={newItem.stock_quantity}
                                            onChangeText={(text) => setNewItem({ ...newItem, stock_quantity: text })}
                                        />
                                    </View>
                                )}

                                <Text style={styles.inputLabel}>Type</Text>
                                <View style={styles.row}>
                                    <TouchableOpacity
                                        style={[styles.typeButton, newItem.item_type === 'product' && styles.typeButtonSelected]}
                                        onPress={() => setNewItem({ ...newItem, item_type: 'product' })}
                                    >
                                        <Text style={[styles.typeButtonText, newItem.item_type === 'product' && styles.typeButtonTextSelected]}>Product</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[styles.typeButton, newItem.item_type === 'service' && styles.typeButtonSelected]}
                                        onPress={() => setNewItem({ ...newItem, item_type: 'service' })}
                                    >
                                        <Text style={[styles.typeButtonText, newItem.item_type === 'service' && styles.typeButtonTextSelected]}>Service</Text>
                                    </TouchableOpacity>
                                </View>

                                {newItem.item_type === 'service' && (
                                    <View style={{ marginTop: 10 }}>
                                        <Text style={styles.inputLabel}>Duration Estimate</Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="e.g. 1 hour, or 2 days"
                                            value={newItem.service_config.duration_estimate}
                                            onChangeText={(text) => setNewItem({
                                                ...newItem,
                                                service_config: { ...newItem.service_config, duration_estimate: text }
                                            })}
                                        />
                                        <Text style={styles.inputLabel}>Location Type</Text>
                                        <View style={styles.row}>
                                            {['In-Person', 'Online', 'Hybrid'].map(loc => (
                                                <TouchableOpacity
                                                    key={loc}
                                                    style={[styles.typeButton, newItem.service_config.location_type === loc && styles.typeButtonSelected]}
                                                    onPress={() => setNewItem({
                                                        ...newItem,
                                                        service_config: { ...newItem.service_config, location_type: loc }
                                                    })}
                                                >
                                                    <Text style={[styles.typeButtonText, newItem.service_config.location_type === loc && styles.typeButtonTextSelected]}>{loc}</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>
                                )}

                                <Text style={styles.inputLabel}>Item Category</Text>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryContainer}>
                                    {['General', 'Electronics', 'Clothing', 'Food', 'Home', 'Beauty', 'Books', 'Services', 'Custom'].map((cat) => (
                                        <TouchableOpacity
                                            key={cat}
                                            style={[
                                                styles.categoryChip,
                                                newItem.category === cat && styles.categoryChipSelected,
                                                { marginVertical: 8 }
                                            ]}
                                            onPress={() => setNewItem({ ...newItem, category: cat })}
                                        >
                                            <Text style={[
                                                styles.categoryChipText,
                                                newItem.category === cat && styles.categoryChipTextSelected
                                            ]}>{cat}</Text>
                                        </TouchableOpacity>
                                    ))}
                                </ScrollView>

                                <Text style={styles.inputLabel}>Item Description</Text>
                                <TextInput
                                    style={[styles.input, styles.textArea]}
                                    placeholder="Short summary of the product or service..."
                                    multiline
                                    value={newItem.description}
                                    onChangeText={(text) => setNewItem({ ...newItem, description: text })}
                                />

                                <Text style={styles.inputLabel}>Item Images</Text>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.itemImagesList}>
                                    {newItem.images.map((img, i) => (
                                        <Image key={i} source={{ uri: getImageUrl(img) }} style={styles.itemImageThumb} resizeMode="contain" />
                                    ))}
                                    <TouchableOpacity style={styles.addItemImageButton} onPress={pickItemImage}>
                                        <Ionicons name="camera-outline" size={24} color="#6366F1" />
                                    </TouchableOpacity>
                                </ScrollView>

                                <TouchableOpacity
                                    style={[styles.saveItemButton, itemLoading && styles.submitButtonDisabled]}
                                    onPress={handleSaveItem}
                                    disabled={itemLoading}
                                >
                                    {itemLoading ? (
                                        <ActivityIndicator color="#FFFFFF" />
                                    ) : (
                                        <Text style={styles.saveItemButtonText}>
                                            {newItem.id && !String(newItem.id).startsWith('temp_') ? 'Update Item' : 'Add Item'}
                                        </Text>
                                    )}
                                </TouchableOpacity>
                            </ScrollView>
                        </View>
                    </View>
                </Modal>
            </View>
        </KeyboardAvoidingView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F5F3FF', // Soft Purple tint
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#FFFFFF',
        paddingTop: Platform.OS === 'android' ? 35 : 10,
    },
    backButton: {
        marginRight: 12,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
    },
    scrollContent: {
        padding: 16,
    },
    logoSection: {
        alignItems: 'center',
        marginBottom: 15,
    },
    logoContainer: {
        width: 80,
        height: 80,
        borderRadius: 20,
        backgroundColor: '#FFFFFF',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        ...getShadow('sm'),
    },
    logo: {
        width: 80,
        height: 80,
        borderRadius: 20,
    },
    logoPlaceholder: {
        alignItems: 'center',
    },
    logoPlaceholderText: {
        fontSize: 10,
        color: '#9CA3AF',
        marginTop: 4,
    },
    logoHint: {
        fontSize: 11,
        color: '#6B7280',
        marginTop: 6,
    },
    form: {
        marginTop: 5,
    },
    inputLabel: {
        fontSize: 12,
        fontWeight: '600',
        color: '#475569',
        marginBottom: 6,
        marginTop: 12,
        marginLeft: 2,
    },
    input: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 10,
        padding: 10,
        fontSize: 14,
        color: '#111827',
    },
    textArea: {
        height: 80,
        textAlignVertical: 'top',
    },
    categoryContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginHorizontal: -3,
    },
    categoryChip: {
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 16,
        margin: 3,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    categoryChipSelected: {
        backgroundColor: '#7C3AED',
        borderColor: '#7C3AED',
    },
    categoryChipText: {
        fontSize: 12,
        color: '#64748B',
    },
    categoryChipTextSelected: {
        color: '#FFFFFF',
        fontWeight: '600',
    },
    photosSection: {
        marginBottom: 5,
    },
    photosList: {
        flexDirection: 'row',
        marginTop: 4,
    },
    photoWrapper: {
        marginRight: 10,
        position: 'relative',
    },
    photoThumb: {
        width: 80,
        height: 60,
        borderRadius: 10,
        backgroundColor: '#FFFFFF',
    },
    removePhoto: {
        position: 'absolute',
        top: -6,
        right: -6,
        backgroundColor: '#FFFFFF',
        borderRadius: 9,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    addPhotoButton: {
        width: 80,
        height: 60,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#7C3AED',
        borderStyle: 'dashed',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F3FF',
    },
    sectionDivider: {
        height: 1,
        backgroundColor: '#E2E8F0',
        marginVertical: 20,
    },
    itemsSection: {
        marginBottom: 8,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    sectionSubtitle: {
        fontSize: 15,
        fontWeight: '800',
        color: '#111827',
    },
    addItemButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F5F3FF',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
        gap: 3,
    },
    addItemText: {
        color: '#7C3AED',
        fontWeight: '800',
        fontSize: 12,
    },
    noItemsText: {
        color: '#94A3B8',
        textAlign: 'center',
        fontStyle: 'italic',
        fontSize: 12,
        marginVertical: 8,
    },
    itemCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        padding: 4,
        borderRadius: 12,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        ...getShadow('xs'),
    },
    itemCardContent: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        padding: 6,
    },
    itemThumb: {
        width: 44,
        height: 44,
        borderRadius: 8,
        backgroundColor: '#F1F5F9',
        resizeMode: 'contain',
    },
    itemInfo: {
        flex: 1,
        marginLeft: 10,
    },
    itemName: {
        fontSize: 13,
        fontWeight: '700',
        color: '#111827',
    },
    itemPrice: {
        fontSize: 12,
        color: '#7C3AED',
        fontWeight: '600',
        marginTop: 1,
    },
    deleteItem: {
        padding: 6,
    },
    submitButton: {
        backgroundColor: '#7C3AED',
        paddingVertical: 12,
        borderRadius: 12,
        alignItems: 'center',
        marginTop: 24,
        marginBottom: 30,
        ...getShadow('md'),
    },
    submitButtonDisabled: {
        opacity: 0.6,
    },
    submitButtonText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: 20,
        maxHeight: '85%',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 15,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
    },
    modalForm: {
        marginBottom: 15,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    typeButton: {
        flex: 1,
        paddingVertical: 8,
        alignItems: 'center',
        borderRadius: 10,
        backgroundColor: '#F1F5F9',
        marginHorizontal: 3,
        marginTop: 6,
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    typeButtonSelected: {
        backgroundColor: '#7C3AED',
        borderColor: '#7C3AED',
    },
    typeButtonText: {
        color: '#64748B',
        fontWeight: '600',
        fontSize: 12,
    },
    typeButtonTextSelected: {
        color: '#FFFFFF',
        fontWeight: '700',
    },
    itemImagesList: {
        marginTop: 4,
        marginBottom: 8,
    },
    itemImageThumb: {
        width: 64,
        height: 64,
        borderRadius: 8,
        marginRight: 8,
        backgroundColor: '#F1F5F9',
    },
    addItemImageButton: {
        width: 64,
        height: 64,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#7C3AED',
        borderStyle: 'dashed',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F3FF',
    },
    saveItemButton: {
        backgroundColor: '#7C3AED',
        paddingVertical: 12,
        borderRadius: 12,
        alignItems: 'center',
        marginTop: 15,
        marginBottom: 15,
    },
    saveItemButtonText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
});

export default CreateBusinessScreen;
